import apiClient from '@/core/api/client';
import { API_ENDPOINTS } from '@/core/api/endpoints';
import { EmployeeInviteRequest, EmployeeResponse, EmployeeListResponse } from '../types';

/**
 * Employee API Service
 */
export const employeeApi = {
    /**
     * Invite a new employee
     */
    inviteEmployee: async (schoolSlug: string, data: EmployeeInviteRequest): Promise<EmployeeResponse> => {
        try {
            const formData = new FormData();
            formData.append('name', data.name);
            formData.append('email', data.email);
            formData.append('password', data.password);
            formData.append('department_ids', JSON.stringify(data.department_ids));
            if (data.phone_number) formData.append('phone_number', data.phone_number);
            if (data.designation) formData.append('designation', data.designation);
            
            if (data.photo) {
                formData.append('photo', data.photo);
            }

            const config = { headers: { 'Content-Type': 'multipart/form-data' } };

            const response = await apiClient.post<EmployeeResponse>(API_ENDPOINTS.EMPLOYEES.INVITE(schoolSlug), formData, config);
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to add employee'
            };
        }
    },

    /**
     * Get all employees for a school (with search, department filter, pagination, and sorting)
     */
    getEmployees: async (
        schoolSlug: string,
        params?: {
            page?: number;
            page_size?: number;
            department_id?: string;
            search?: string;
            sort_by?: string;
            sort_order?: 'asc' | 'desc';
        }
    ): Promise<EmployeeListResponse> => {
        try {
            const cleanParams: Record<string, any> = {};
            if (params) {
                if (params.page !== undefined) cleanParams.page = params.page;
                if (params.page_size !== undefined) cleanParams.page_size = params.page_size;
                if (params.department_id) cleanParams.department_id = params.department_id;
                if (params.search && params.search.trim()) cleanParams.search = params.search.trim();
                if (params.sort_by) cleanParams.sort_by = params.sort_by;
                if (params.sort_order) cleanParams.sort_order = params.sort_order;
            }
            const response = await apiClient.get<EmployeeListResponse>(API_ENDPOINTS.EMPLOYEES.LIST(schoolSlug), { params: cleanParams });
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to fetch employees'
            };
        }
    },

    updateEmployee: async (schoolSlug: string, employeeId: string, data: Partial<EmployeeInviteRequest>): Promise<EmployeeResponse> => {
        try {
            const formData = new FormData();
            if (data.name) formData.append('name', data.name);
            if (data.email) formData.append('email', data.email);
            if (data.password) formData.append('password', data.password);
            
            if (data.department_ids) {
                formData.append('department_ids', JSON.stringify(data.department_ids));
            }
            if (data.phone_number !== undefined) formData.append('phone_number', data.phone_number || '');
            if (data.designation !== undefined) formData.append('designation', data.designation || '');
            
            if (data.photo !== undefined) {
                if (data.photo === null) {
                    // depending on backend, you might need a way to clear photo
                    formData.append('photo', '');
                } else {
                    formData.append('photo', data.photo);
                }
            }

            const config = { headers: { 'Content-Type': 'multipart/form-data' } };

            const response = await apiClient.patch<EmployeeResponse>(API_ENDPOINTS.EMPLOYEES.UPDATE(schoolSlug, employeeId), formData, config);
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to update employee'
            };
        }
    },

    deleteEmployee: async (schoolSlug: string, employeeId: string): Promise<EmployeeResponse> => {
        try {
            const response = await apiClient.delete<EmployeeResponse>(API_ENDPOINTS.EMPLOYEES.DELETE(schoolSlug, employeeId));
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to delete employee'
            };
        }
    },

    /**
     * Download import template
     */
    downloadTemplate: async (schoolSlug: string, type: string): Promise<any> => {
        try {
            const response = await apiClient.get(
                API_ENDPOINTS.IMPORT_EXPORT.DOWNLOAD_TEMPLATE(schoolSlug, type),
                { responseType: 'blob' }
            );
            return {
                success: true,
                data: response.data,
                headers: response.headers
            };
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to download template'
            };
        }
    },

    /**
     * Import employees from file
     */
    importFile: async (schoolSlug: string, type: string, file: File): Promise<any> => {
        try {
            const formData = new FormData();
            formData.append('file', file);
            
            const response = await apiClient.post(
                API_ENDPOINTS.IMPORT_EXPORT.IMPORT_FILE(schoolSlug, type),
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to import file'
            };
        }
    }
};
